/**
 * Frontend client for presets (server/modules/presets): the workspace's own
 * saves (metals, gems, environments, Looks, material files added from the
 * studio's preset panels) and, on read, the curated system library.
 */
import { apiGet, apiPost, apiDelete } from "../shared/apiClient";

/**
 * Mirrors `PresetKind` in server/modules/presets/preset.model.ts — keep the two
 * in step (`verify:presets` compares them). The first five are the original
 * workspace-save kinds; the rest are the library categories.
 */
export type PresetKind =
  | "metal" | "gem" | "env" | "fx" | "look"
  | "background" | "gemEnv" | "ground" | "stage" | "scene" | "pose" | "lut" | "onBody"
  | "ceramic" | "pearl" | "other";

export interface Preset {
  _id: string;
  /** Null for a system preset — it belongs to the platform, not a tenant. */
  workspaceId: string | null;
  /** `system` = curated by us and read-only to workspaces. Absent on presets
   *  saved before the curated library existed, which are all workspace-owned. */
  scope?: "system" | "workspace";
  kind: PresetKind;
  name: string;
  values: Record<string, unknown>;
  fileUrl?: string | null;
  thumbUrl?: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const listPresets = (kind?: PresetKind) =>
  apiGet<Preset[]>(`/presets${kind ? `?kind=${kind}` : ""}`);

/**
 * The server's `createPresetSchema` has always accepted the file fields; only
 * this type left them out, so a file-backed workspace preset (an HDR Studio
 * environment: `fileUrl` = the baked .hdr, `thumbUrl` = its poster) could not
 * be written from the client without a cast.
 */
export interface CreatePresetInput {
  kind: PresetKind;
  name: string;
  values: Record<string, unknown>;
  fileUrl?: string;
  thumbUrl?: string;
  assetId?: string;
  tags?: string[];
}

export const createPreset = (input: CreatePresetInput) =>
  apiPost<Preset>("/presets", input);

/** Only a workspace's own preset can be deleted; the server refuses a system one. */
export const deletePreset = (id: string) =>
  apiDelete<{ id: string }>(`/presets/${id}`);
